import { useEffect, useRef, useState } from "react";
import * as tus from "tus-js-client";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge, btnDanger, btnPrimary, btnSecondary, inputCls, statusTone } from "@/components/admin/ui";

export const TrailerEditor = ({ festivalId, trailer, onChange }) => {
  const t = trailer || { type: "none", status: "none" };
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);
  const pending = t.type === "bunny" && ["uploading", "processing"].includes(t.status);

  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/festivals/${festivalId}/trailer/status`);
        if (["ready", "error"].includes(data.status)) {
          const { data: fresh } = await api.get(`/admin/festivals/${festivalId}`);
          onChange(fresh);
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [pending, festivalId, onChange]);

  const addYoutube = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/festivals/${festivalId}/trailer/youtube`, { url });
      onChange(data);
      setUrl("");
      toast.success("Trailer added");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const { data: s } = await api.post(`/admin/festivals/${festivalId}/trailer/upload-session`);
      await new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: "https://video.bunnycdn.com/tusupload",
          headers: {
            AuthorizationSignature: s.signature,
            AuthorizationExpire: String(s.expiration_time),
            VideoId: s.bunny_video_id,
            LibraryId: String(s.library_id),
          },
          metadata: { filetype: file.type, title: file.name },
          retryDelays: [0, 3000, 5000, 10000, 20000],
          onProgress: (sent, total) => setProgress(Math.round((sent / total) * 100)),
          onError: reject,
          onSuccess: resolve,
        });
        upload.start();
      });
      await api.post(`/admin/festivals/${festivalId}/trailer/uploaded`, { bunny_video_id: s.bunny_video_id });
      const { data: fresh } = await api.get(`/admin/festivals/${festivalId}`);
      onChange(fresh);
      toast.success("Trailer uploaded — encoding now");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this festival trailer?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/festivals/${festivalId}/trailer`);
      onChange(data);
      toast.success("Trailer removed");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="festival-trailer-editor">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-[15px] font-semibold">Festival Trailer</p>
          <p className="text-[12px] text-[#6B7280]">Plays at the top of the festival page. Upload a video file or paste a YouTube link.</p>
        </div>
        <div className="flex items-center gap-2">
          {t.type !== "none" && <Badge tone={statusTone(t.status)}>{t.status}</Badge>}
          {t.type !== "none" && (
            <button type="button" className={btnDanger} onClick={remove} disabled={busy} data-testid="remove-trailer-button">
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 items-start">
        <div className="aspect-video rounded-md overflow-hidden bg-[#111] flex items-center justify-center" data-testid="trailer-preview">
          {t.embed_url ? (
            <iframe src={t.embed_url} title="Festival trailer" className="w-full h-full" allow="encrypted-media; fullscreen" allowFullScreen />
          ) : (
            <p className="text-[12px] text-white/60 px-4 text-center">{pending ? "Encoding…" : "No trailer yet"}</p>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <p className="text-[13px] font-semibold mb-1.5">Upload a trailer file</p>
            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="trailer-file-input" />
            <button type="button" className={btnSecondary} onClick={() => fileRef.current?.click()} disabled={busy} data-testid="trailer-upload-button">
              {progress !== null ? `Uploading ${progress}%` : "Choose video file"}
            </button>
            {progress !== null && (
              <div className="mt-2 h-1.5 w-[220px] rounded bg-[#E5E7EB] overflow-hidden">
                <div className="h-full bg-[#F8D481] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
          <div>
            <p className="text-[13px] font-semibold mb-1.5">…or paste a YouTube link</p>
            <div className="flex gap-2">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addYoutube()}
                placeholder="https://youtube.com/watch?v=…"
                className={`${inputCls} w-full sm:max-w-[360px]`}
                data-testid="trailer-youtube-input"
              />
              <button type="button" className={btnPrimary} onClick={addYoutube} disabled={busy || !url.trim()} data-testid="trailer-youtube-button">
                Use link
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
